import React, { useEffect } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { Form, useWatch, useForm, isEmpty } from '../../../../form'
import { useTranslation } from 'react-i18next'
import { withStyles } from '@material-ui/core/styles'
import { getInvoiceUserEmail, getUserCompanyUpdateInfo } from '../../../../actions'
import { invoiceDeliveryAndDetailsValidate } from '../../../../containers/validate'
import Button from '@material-ui/core/Button'
import { getInvoicesList } from '../../../../selectors/invoice.selector'
import { InvoiceDeliveryMethod } from './invoiceDeliveryForm.component'
import { InvoiceDetails } from './invoiceDetailsForm.component'
import { InvoiceAdditionalInformation } from './invoiceAdditionalInfoForm.component'
import classNames from 'classnames'
import mobileView from '../../../../utils/mobile.utils'

const styles = (theme) => ({
  select: {
    fontSize: '14px',
    marginTop: theme.spacing(4),
    cursor: 'pointer',
    border: '1px solid gray'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  resize: {
    fontSize: '14px'
  },
  checkbox: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#808080',
    marginBottom: '10px'
  },
  button: {
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    borderRadius: '10px'
  },
  buttonBack: {
    padding: !mobileView ? '2px 30px' : '4px 20px',
    color: 'rgb(83 77 77)',
    backgroundColor: '#fff',
    border: '2px solid #44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#E0F7FA'
    }
  },
  buttonNext: {
    padding: !mobileView ? '0 25px' : '2px 20px',
    color: '#fff',
    backgroundColor: '#44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#3BA9B5'
    }
  }
})

const InvoiceDeliveryAndDetailsForm = ({
  classes,
  goBack,
  goNext,
  formValues
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { invoiceOperators } = useSelector(
    (state) => state.invoice,
    shallowEqual
  )
  const invoices = useSelector(getInvoicesList)

  const _invoiceOperators = ['', ...invoiceOperators]

  const form = useForm({
    name: 'invoice',
    onSubmit: onSubmit,
    defaultValues: formValues,
    validator: (values) => invoiceDeliveryAndDetailsValidate(values),
    keepEmptyValuesOnSubmit: false
  })

  useEffect(() => {
    form.reset(formValues)
  }, [formValues])

  const {
    control,
    formState: { errors }
  } = form

  const [attemptingSubmit, setAttemptingSubmit] = React.useState(false)

  function onSubmit(values) {
    goNext(values)
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  function clearErrors(fields) {
    if (fields) {
      form?.clearErrors(fields)
    } else {
      form?.clearErrors()
    }
  }

  const current_date = new Date()
  const watch = useWatch({
    fields: [
      'company_name',
      'business_id',
      'total_sum',
      'groupTotalAmount',
      'groupInvoiceRow',
      'quick_pay',
      'groupInvoice',
      'vat_selection',
      'customer_type',
      'rows'
    ],
    control
  })
  const company_name = watch.company_name
  const business_id = watch.business_id
  const groupTotalAmount = parseFloat(watch.groupTotalAmount)
  const groupInvoice = watch.groupInvoice

  let quick_pay_overdue_by_customer = []
  if (company_name !== '' && business_id !== '') {
    quick_pay_overdue_by_customer = invoices.filter(
      (inv) =>
        inv.instant_payment === 'quick_pay' &&
        (inv.status === 3 || inv.status === 5) &&
        inv.remindersent === 1 &&
        current_date > new Date(inv.due_date) &&
        inv.business_id === business_id
    )
  }
  let disable_quick_pay
  if (quick_pay_overdue_by_customer.length >= 1) {
    disable_quick_pay = true
  } else {
    disable_quick_pay = false
  }

  let isGroupInvoiceEnabled = groupInvoice ? true : false

  const isGroupInvoice = isGroupInvoiceEnabled || groupTotalAmount > 0

  React.useEffect(() => {
    if (attemptingSubmit && !isEmpty(errors)) {
      setAttemptingSubmit(false)
    }
  }, [attemptingSubmit, errors])

  React.useEffect(() => {
    dispatch(getInvoiceUserEmail(0))
    dispatch(getUserCompanyUpdateInfo())
  }, [])

  return (
    <div>
      <Form {...form}>
        <div className="row" style={{ padding: '0 0 0 25px' }}>
          <div className="dashboard-content-header">
            <div className="col-xs-12 col-sm-4 col-lg-4">
              <InvoiceDeliveryMethod
                classes={classes}
                invoiceOperators={_invoiceOperators}
                clearErrors={clearErrors}
                control={control}
              />
            </div>
            <div className="col-xs-12 col-sm-4 col-lg-4">
              <InvoiceDetails
                classes={classes}
                setValue={setValue}
                control={control}
              />
            </div>
            <div className="col-xs-12 col-sm-4 col-lg-4">
              <InvoiceAdditionalInformation
                classes={classes}
                form={form}
                setValue={setValue}
                control={control}
                disable_quick_pay={disable_quick_pay}
                isGroupInvoice={isGroupInvoice}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'flex-end',
            margin: '40px 10px 15px'
          }}
        >
          <Button
            className={classNames(classes.buttonBack, classes.button)}
            onClick={() => goBack(form.getValues())}
          >
            {t('asiakkaat.back')}
          </Button>
          <Button
            className={classNames(classes.buttonNext, classes.button)}
            type="submit"
            onClick={() => setAttemptingSubmit(true)}
          >
            {t('asiakkaat.next')}
            <span
              style={{
                display: 'inline-block',
                transform: 'scaleX(1.5)',
                fontSize: '18px',
                letterSpacing: '-2px'
              }}
            >
              &nbsp; &gt;
            </span>
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default withStyles(styles)(InvoiceDeliveryAndDetailsForm)
